/**
 * `CompendiumArchive` — the **COMPENDIUM archive**, an in-fiction OMNINET compendium browser that
 * runs as the wide COMPENDIUM rail page (`decisions/0203`): a section strip
 * (MECHS / PILOTS / NPCS / GLOSSARY), a category rail, maker faceting + grouping, a global
 * omninet query, and brutalist cross-linked record sheets — every record read off the real
 * {@link Compendium} via {@link CompendiumSource}.
 *
 * The shell's rail panel is the frame; this component owns the archive's navigation state and
 * content, and lays itself out from the `size` the panel measures for it.
 *
 * Read-only by construction: it never dispatches a command, only browses loaded packs. NPC
 * categories are empty until a GM's `.lcp` pack lands (the "NO … LOADED" empty state).
 */
import { Icon } from "../../lib/Icon";
import "../../lib/searchField.css";
import { tipAttrs } from "../../lib/tooltip";
import { faArrowLeft, faChevronRight, faLayerGroup, faMagnifyingGlass, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useMemo, useReducer, useRef, useState, type CSSProperties, type RefObject } from "react";
import { useCompendiumContext } from "../../lib/CompendiumProvider";
import type { CompendiumPage } from "../home/railState";
import {
  CompendiumSource,
  INITIAL_STATE,
  PACK_LINE,
  SECTIONS,
  type CompendiumState,
  type CompendiumType,
  type Ref,
  type SectionId,
} from "./compendiumData";
import { buildDetail, computeBrowse, computeSearch, layoutFor, moveFocus, orderedRefs, sectionOf } from "./compendiumView";
import { DetailPane } from "./CompendiumDetail";
import type { NpcTier } from "../../lib/npcTierText";
import { ValText } from "./valSlot";
import "./compendium.css";

const MONO = "'JetBrains Mono', monospace";
const SANS = "'Archivo', sans-serif";

// The layout (compact single-column ↔ three-pane, and whether the rail shows) reflows purely
// from the live measured width via `layoutFor` — there is no manual toggle.

// ---- state reducer (the window's navigation) ----------------------------------------

type Action =
  | { t: "section"; id: SectionId; first: CompendiumType }
  | { t: "category"; type: CompendiumType }
  | { t: "select"; ref: Ref }
  | { t: "nav"; ref: Ref }
  | { t: "clearSelection" }
  | { t: "toggleGroup" }
  | { t: "facet"; axis: string; val: string }
  | { t: "filter"; v: string }
  | { t: "search"; v: string }
  | { t: "clearSearch" }
  | { t: "syncFromUrl"; page: { section: SectionId; category: CompendiumType; selected: Ref | null } };

const sameRef = (a: Ref | null, b: Ref | null) => a?.type === b?.type && a?.id === b?.id;

/** The URL-bearing slice of COMPENDIUM state — the triple that round-trips through the address bar. */
const pageOf = (s: CompendiumState): CompendiumPage => ({ section: s.section, category: s.category, selected: s.selected });

const samePage = (a: CompendiumPage | undefined, b: CompendiumPage) =>
  a != null && a.section === b.section && a.category === b.category && sameRef(a.selected, b.selected);

/** Reducer actions that change the URL-bearing triple (so a writeback to the URL is owed). */
const DURABLE_ACTIONS = new Set<Action["t"]>(["section", "category", "select", "nav", "clearSelection"]);

function reducer(s: CompendiumState, a: Action): CompendiumState {
  switch (a.t) {
    case "syncFromUrl":
      // Pull the durable triple in from the URL (back/forward/reload or a history button).
      return { ...s, section: a.page.section, category: a.page.category, selected: a.page.selected };
    case "section":
      return { ...s, section: a.id, category: a.first, selected: null, facets: {}, filter: "", searchText: "" };
    case "category":
      return { ...s, category: a.type, selected: null, facets: {}, filter: "" };
    // `select` (a list/search pick) and `nav` (a cross-reference link) both open a record and
    // re-derive the section + category from the record's type, so the top section strip and the
    // side rail always show the path to whatever's open — even after following a link across
    // sections (e.g. a weapon → its tag in GLOSSARY). The back/forward trail lives in the window
    // (a COMPENDIUM-local history), not in the reducer.
    case "select":
    case "nav":
      return { ...s, selected: a.ref, searchText: "", section: sectionOf(a.ref.type), category: a.ref.type };
    case "clearSelection":
      return { ...s, selected: null };
    case "toggleGroup":
      return { ...s, groupBy: !s.groupBy };
    case "facet": {
      // Toggle one axis independently: clicking the active value clears that axis; otherwise
      // it replaces that axis's selection. Other axes (and so other active facets) are untouched.
      const next = { ...s.facets };
      if (next[a.axis] === a.val) delete next[a.axis];
      else next[a.axis] = a.val;
      return { ...s, facets: next };
    }
    case "filter":
      return { ...s, filter: a.v };
    case "search":
      return { ...s, searchText: a.v };
    case "clearSearch":
      return { ...s, searchText: "" };
  }
}

// ---- the app ------------------------------------------------------------------------

export function CompendiumArchive({
  size,
  page,
  onPage,
}: {
  /** The panel's measured content box — the layout reflows from its width. */
  size: { w: number; h: number };
  /**
   * The URL-bearing navigation: `section`/`category`/`selected`. The shell owns this in the
   * address bar via `useRail`; COMPENDIUM renders it and reports changes back through `onPage`.
   * The reducer keeps owning the ephemeral remainder (groupBy/facet/filter/searchText); the
   * back/forward trail is the archive-local `history` ref.
   */
  page: CompendiumPage;
  onPage: (next: CompendiumPage) => void;
}) {
  const { compendium } = useCompendiumContext();
  const source = useMemo(() => new CompendiumSource(compendium), [compendium]);

  // COMPENDIUM-local navigation history (the ◄ / ► toolbar buttons). The *browser's* back/forward
  // walks rail pages, a timeline that mixes in every other page's navigation — useless for
  // stepping through the archive alone. This is a separate, compendium-only trail of the pages
  // this archive has visited (`entries` + a `cursor`). We still write the current page to the
  // URL (reload/deep-link), but the trail itself is ours.
  const history = useRef<{ entries: CompendiumPage[]; cursor: number }>({ entries: [page], cursor: 0 });
  const [, bumpHist] = useState(0);
  // Set just before a history-driven `onPage` so the recorder below skips re-recording it.
  const fromHistoryNav = useRef(false);

  // Scroll `row` into view by adjusting ONLY its nearest overflow-y:auto/scroll ancestor.
  // Never `scrollIntoView`: that walks and scrolls EVERY scrollable ancestor — including the
  // desktop workspace, which is `overflow: hidden` (still programmatically scrollable) — so a
  // cross-link landing on an off-screen row inside a fullscreen-snapped window shifts the whole
  // desktop up, pushing every card's top out of the viewport.
  const scrollRowIntoPane = (row: HTMLElement | null): void => {
    if (!row) return;
    let pane: HTMLElement | null = row.parentElement;
    while (pane) {
      const oy = getComputedStyle(pane).overflowY;
      if ((oy === "auto" || oy === "scroll") && pane.scrollHeight > pane.clientHeight) break;
      pane = pane.parentElement;
    }
    if (!pane) return;
    const paneRect = pane.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    if (rowRect.top < paneRect.top) pane.scrollTop += rowRect.top - paneRect.top;
    else if (rowRect.bottom > paneRect.bottom) pane.scrollTop += rowRect.bottom - paneRect.bottom;
  };

  // The active row in the list pane + a one-shot "scroll it into view" flag, armed only by a
  // cross-reference `nav` (see the `nav` handler) — so following a link jumps the list to the
  // landed-on record, while a direct list pick leaves the scroll position alone.
  const activeRowRef = useRef<HTMLButtonElement | null>(null);
  const scrollOnNav = useRef(false);

  // Roving keyboard-focus cursor over the active list. A window-local index into the flat
  // `orderedRefs` sequence of whatever list shape is showing (flat / maker-grouped / search) — NOT
  // reducer or URL state, just ephemeral focus. `-1` = nothing focused yet; the first ArrowDown/Up
  // lands it. `focusedRowRef` points at the currently-focused row so we can `.focus()` it (roving
  // tabindex: the focused row is the list's single tab stop, arrows move focus within).
  const [focusIdx, setFocusIdx] = useState(-1);
  // The active list as a flat ordered ref sequence, refreshed every render (below) so the keyboard
  // handler — registered before the early-return / render computation — always reads the current
  // list. Held in a ref to avoid re-subscribing the global keydown listener on every list change.
  const orderedRefsRef = useRef<Ref[]>([]);
  const focusedRowRef = useRef<HTMLButtonElement | null>(null);
  // Set when a focus move should pull DOM focus onto the row (a keyboard arrow press), so a render
  // caused by anything else (a mouse click, a list change) doesn't steal focus from the user.
  const focusDom = useRef(false);

  const canBack = history.current.cursor > 0;
  const goHistory = (delta: number) => {
    const h = history.current;
    const target = h.entries[h.cursor + delta];
    if (!target) return;
    h.cursor += delta;
    fromHistoryNav.current = true;
    onPage(target);
    bumpHist((v) => v + 1);
  };

  // Seed the reducer's durable triple from the URL on first mount; ephemeral fields start
  // from INITIAL_STATE. After that, the effect below keeps the reducer in sync when the URL
  // (page) changes out from under us — e.g. browser back/forward.
  const [s, rawDispatch] = useReducer(reducer, { ...INITIAL_STATE, ...page });

  // The single dispatch the whole window uses. For a *durable* action it derives the next
  // URL-bearing triple with the pure reducer and writes it back to the address bar (onPage) —
  // the URL is the source of truth for section/category/selected. Ephemeral actions just
  // update the reducer. `syncFromUrl` (the URL→reducer pull) never writes back.
  const dispatch = (a: Action) => {
    if (DURABLE_ACTIONS.has(a.t)) {
      const next = pageOf(reducer(s, a));
      if (next.section !== page.section || next.category !== page.category || !sameRef(next.selected, page.selected)) {
        onPage(next);
      }
    }
    rawDispatch(a);
  };

  // URL → reducer: when the durable triple in the URL differs from the reducer's, pull it in.
  // This is what makes back/forward + reload restore the archive's position. We only reconcile
  // the durable fields; ephemeral view state is untouched.
  useEffect(() => {
    if (s.section !== page.section || s.category !== page.category || !sameRef(s.selected, page.selected)) {
      // The URL moved out from under the reducer — an EXTERNAL navigation (a picker cross-link
      // opening the archive at a record, browser back/forward, a deep link). Unlike an in-list
      // pick, nothing put the now-selected row in view, so arm the one-shot scroll (the same seam
      // the internal `nav` cross-link uses). Only when a record is actually selected.
      if (page.selected) scrollOnNav.current = true;
      rawDispatch({ t: "syncFromUrl", page });
    }
    // Intentionally keyed on the URL triple only — `s` is read but reconciling on its change
    // would loop. The wrapped `dispatch` is what propagates reducer changes back to the URL.
  }, [page.section, page.category, page.selected?.type, page.selected?.id]);

  // Record each page this window lands on into the COMPENDIUM-local history — unless the landing
  // was itself a back/forward step (`fromHistoryNav`). A fresh navigation truncates any
  // forward trail, the standard browser-history shape, scoped to this window.
  useEffect(() => {
    const h = history.current;
    if (fromHistoryNav.current) {
      fromHistoryNav.current = false;
      return;
    }
    if (samePage(h.entries[h.cursor], page)) return;
    h.entries = [...h.entries.slice(0, h.cursor + 1), page];
    h.cursor = h.entries.length - 1;
    bumpHist((v) => v + 1);
  }, [page.section, page.category, page.selected?.type, page.selected?.id]);

  // After a cross-link lands on a record (and only then), jump the list pane so the now-active
  // row is in view — instant, no smooth scroll. Keyed on the selection so it runs once the list
  // has re-rendered on the target's category with the active row mounted.
  useEffect(() => {
    if (!scrollOnNav.current) return;
    scrollOnNav.current = false;
    scrollRowIntoPane(activeRowRef.current);
  }, [s.selected?.type, s.selected?.id]);

  // Keyboard a11y. One global handler for the whole window:
  //  - "/"            → focus the omninet query (unless already typing)
  //  - Escape         → clear search → pop the back-stack → clear selection
  //  - Arrow up/down  → move the roving focus cursor through the active list pane's rows
  //  - Enter          → open the focused row (same as clicking it)
  //  - Backspace      → pop the back-stack (guarded: never while typing in a text field)
  // The arrow/Enter handlers walk `orderedRefsRef` — the active list flattened to one ordered ref
  // sequence (refreshed each render), so the same cursor spans the flat / maker-grouped / search
  // shapes. `setFocusIdx` uses a functional update so the listener stays stable (no `focusIdx` dep).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName ?? "";
      const typing = tag === "INPUT" || tag === "TEXTAREA";
      if (e.key === "/" && !typing) {
        e.preventDefault();
        document.getElementById("compendium-search")?.focus();
      } else if (e.key === "Escape") {
        if (s.searchText) dispatch({ t: "clearSearch" });
        else if (history.current.cursor > 0) goHistory(-1);
        else if (s.selected) dispatch({ t: "clearSelection" });
      } else if ((e.key === "ArrowDown" || e.key === "ArrowUp") && !typing) {
        const refs = orderedRefsRef.current;
        if (refs.length === 0) return;
        e.preventDefault();
        focusDom.current = true; // a keyboard move should pull DOM focus onto the row
        setFocusIdx((i) => moveFocus(refs.length, i, e.key === "ArrowDown" ? 1 : -1));
      } else if (e.key === "Enter" && !typing) {
        const refs = orderedRefsRef.current;
        setFocusIdx((i) => {
          const ref = i >= 0 ? refs[i] : undefined;
          if (ref) dispatch({ t: "select", ref });
          return i;
        });
      } else if (e.key === "Backspace" && !typing) {
        // Pop the back-stack when there's history to pop. Guarded against `typing` above so it
        // never steals a Backspace from the search box or the list filter input.
        if (history.current.cursor > 0) {
          e.preventDefault();
          goHistory(-1);
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [s.searchText, s.selected]);

  // Pull DOM focus onto the roving-focused row, but only when a keyboard arrow armed it
  // (`focusDom`) — so a list re-render or a mouse interaction never yanks focus from the user.
  useEffect(() => {
    if (!focusDom.current) return;
    focusDom.current = false;
    // preventScroll: plain .focus() auto-scrolls ancestors exactly like scrollIntoView — same
    // desktop-shift bug through a different door.
    focusedRowRef.current?.focus({ preventScroll: true });
    scrollRowIntoPane(focusedRowRef.current);
  }, [focusIdx]);

  // The list's row set changes when the category/section/search/facets/filter/grouping change —
  // a held cursor index would then point at a different record. Reset it so the next arrow press
  // restarts from the top of the now-current list.
  useEffect(() => {
    setFocusIdx(-1);
  }, [s.section, s.category, s.searchText, s.filter, s.groupBy, s.facets]);

  // Item 3.2: the archive's own tier selector. It is view state of the record sheet, not of the
  // browse/search reducer, so it lives here as plain local state rather than in `CompendiumState`
  // — nothing about navigation, facets or history depends on it, and adding it to the reducer
  // would make every tier click a history-affecting action.
  const [npcTier, setNpcTier] = useState<NpcTier>(1);

  // Layout reflows purely from the panel's measured width: `wide` is three-pane vs a
  // single stacked column, `railFits` is whether the category rail has room (it folds first).
  const { wide, showRail: railFits } = layoutFor(size.w);
  const searchActive = s.searchText.trim().length > 0;
  const detail = buildDetail(source, s.selected, npcTier);
  const hasItem = detail != null && !searchActive;
  const isWelcome = detail == null && !searchActive;
  const showRail = railFits && !searchActive;
  const compactDetail = !wide && (hasItem || isWelcome);
  const showList = !searchActive && (wide || !compactDetail);
  const showDetail = !searchActive && (wide || compactDetail);
  const showBackBar = !wide && compactDetail && (s.selected != null || canBack);

  const browse = !searchActive ? computeBrowse(source, s) : null;
  const search = searchActive ? computeSearch(source, s.searchText) : null;
  const sections = computeSections(source, s.section);

  // Refresh the flat ordered-ref sequence the keyboard handler walks, from whichever list
  // shape is active, every render. If the cursor now points past the (possibly shrunk/changed)
  // list, snap it back in bounds so a stale index never selects the wrong row.
  const orderedRowRefs = (browse ?? search) ? orderedRefs((browse ?? search)!) : [];
  orderedRefsRef.current = orderedRowRefs;
  const liveFocusIdx = focusIdx >= 0 && focusIdx < orderedRowRefs.length ? focusIdx : -1;

  const select = (ref: Ref) => dispatch({ t: "select", ref });
  // A cross-reference link (`nav`) re-derives the section/category from the target's type,
  // so the list pane re-renders on a different category with the target row possibly off
  // screen — arm a one-shot scroll so it jumps into view. A *direct* list pick (`select`)
  // is already where the user is looking, so it never arms the scroll.
  const nav = (ref: Ref) => {
    scrollOnNav.current = true;
    dispatch({ t: "nav", ref });
  };

  return (
    <div className="compendium-archive">
      {/* section strip + omninet query */}
          <div
            style={{
              display: "flex",
              alignItems: "stretch",
              justifyContent: "space-between",
              borderBottom: "2px solid #111",
              background: "#f6f4ee",
              // flex: none, or a long list below squeezes the strip (it read 26px on MECHS, 38 on MODS).
              flex: "none",
              height: 38,
            }}
          >
            <div style={{ display: "flex" }}>
              {sections.map((sec) => (
                <button
                  key={sec.id}
                  type="button"
                  onClick={() => dispatch({ t: "section", id: sec.id, first: SECTION_FIRST[sec.id] })}
                  style={{
                    border: "none",
                    borderRight: "2px solid #111",
                    background: sec.on ? "#111" : "#f6f4ee",
                    color: sec.on ? "#fff" : "#111",
                    fontFamily: MONO,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 1,
                    padding: "0 15px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                  }}
                >
                  {sec.id}
                  <span style={{ fontSize: 9, fontWeight: 500, color: sec.on ? "#9a978f" : "#b3ad9f" }}>{sec.count}</span>
                </button>
              ))}
            </div>
            {/* The archive-wide query: the same field as a list's filter, inset in the strip. */}
            <div className="search-field compendium-search">
              <Icon icon={faMagnifyingGlass} className="search-field-icon" />
              <input
                id="compendium-search"
                value={s.searchText}
                onChange={(e) => dispatch({ t: "search", v: e.target.value })}
                placeholder="search everything…"
                aria-label="Search the compendium"
                style={{ width: wide ? 200 : 132 }}
              />
              {searchActive ? (
                <button type="button" className="search-field-clear" aria-label="Clear search" {...tipAttrs({ label: "Clear search" })} onClick={() => dispatch({ t: "clearSearch" })}>
                  <Icon icon={faXmark} />
                </button>
              ) : (
                <kbd className="search-field-key">/</kbd>
              )}
            </div>
          </div>

          {/* body — fills the rest of the panel below the section strip; its panes scroll
              internally. */}
          <div className="compendium-archive-body">
            {/* RAIL (wide only) */}
            {showRail && browse && (
              <div
                className="compendium-scroll"
                style={{ flex: "none", width: 194, borderRight: "2px solid #111", background: "#f1eee6", overflowY: "auto", display: "flex", flexDirection: "column" }}
              >
                <div style={{ padding: "13px 14px 9px", fontFamily: MONO, fontSize: 9, letterSpacing: 2, color: "#8a857a", borderBottom: "1px solid #d8d3c7" }}>
                  {browse.sectionName}
                </div>
                {browse.rail.map((r) => (
                  <button
                    key={r.type}
                    type="button"
                    className="compendium-hover-rail"
                    onClick={() => dispatch({ t: "category", type: r.type })}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      border: "none",
                      borderBottom: "1px solid #ddd8cc",
                      background: r.on ? "#fff" : "transparent",
                      color: "#111",
                      cursor: "pointer",
                      padding: "11px 14px",
                      textAlign: "left",
                      fontFamily: SANS,
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: r.on ? 800 : 500, letterSpacing: ".2px" }}>{r.label}</span>
                    <span style={{ fontFamily: MONO, fontSize: 10, color: r.on ? "#c0240f" : "#a8a294" }}>{r.count}</span>
                  </button>
                ))}
                {browse.canGroup && (
                  <button
                    type="button"
                    className="compendium-hover-soft"
                    onClick={() => dispatch({ t: "toggleGroup" })}
                    style={{
                      margin: "13px 12px",
                      border: "2px solid #111",
                      background: s.groupBy ? "#111" : "#fff",
                      color: s.groupBy ? "#fff" : "#111",
                      cursor: "pointer",
                      padding: "8px 10px",
                      fontFamily: MONO,
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: 1,
                      textAlign: "left",
                    }}
                  >
                    <Icon icon={faLayerGroup} className="icon-lead" />GROUP BY MAKER
                  </button>
                )}
                <div style={{ flex: 1 }} />
                <div style={{ padding: "11px 14px", borderTop: "1px solid #d8d3c7", fontFamily: MONO, fontSize: 9, letterSpacing: 1, color: "#a39e92" }}>
                  PACKS · {PACK_LINE}
                </div>
              </div>
            )}

            {/* SEARCH RESULTS */}
            {searchActive && search && <SearchPane search={search} onPick={select} focusIdx={liveFocusIdx} focusedRowRef={focusedRowRef} />}

            {/* LIST PANE */}
            {showList && browse && (
              <ListPane
                browse={browse}
                wide={wide}
                state={s}
                dispatch={dispatch}
                onPick={select}
                activeRowRef={activeRowRef}
                focusIdx={liveFocusIdx}
                focusedRowRef={focusedRowRef}
              />
            )}

            {/* DETAIL PANE */}
            {showDetail && (
              <div className="compendium-scroll" style={{ flex: 1, overflowY: "auto", minWidth: 0, position: "relative", background: "#fff" }}>
                {showBackBar && (
                  <div
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 6,
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "9px 16px",
                      background: "#111",
                      color: "#fff",
                      borderBottom: "2px solid #111",
                    }}
                  >
                    <button
                      type="button"
                      className="compendium-hover-ghost"
                      onClick={() => (canBack ? goHistory(-1) : dispatch({ t: "clearSelection" }))}
                      style={{ border: "1px solid #fff", background: "none", color: "#fff", cursor: "pointer", fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: 1, padding: "5px 10px" }}
                    >
                      <Icon icon={faArrowLeft} className="icon-lead" />BACK
                    </button>
                    <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: 1, color: "#bbb" }}>
                      {canBack ? "RETURN TO PREVIOUS" : "RETURN TO LIST"}
                    </span>
                  </div>
                )}
                {isWelcome && <WelcomePane source={source} />}
                {hasItem && detail && <DetailPane d={detail} onNav={nav} tier={{ value: npcTier, onTier: setNpcTier }} />}
              </div>
            )}
          </div>
    </div>
  );
}

// ---- panes --------------------------------------------------------------------------

function SearchPane({
  search,
  onPick,
  focusIdx,
  focusedRowRef,
}: {
  search: ReturnType<typeof computeSearch>;
  onPick: (ref: Ref) => void;
  /** Roving keyboard-focus index over the flattened result rows (group order), or -1. */
  focusIdx: number;
  /** Attached to the focused row so a keyboard move can `.focus()` it (roving tabindex). */
  focusedRowRef: RefObject<HTMLButtonElement | null>;
}) {
  // Running index across groups — must match `orderedRefs(SearchView)`'s flatten order so the
  // roving cursor lands on the same row the keyboard handler selects.
  let i = -1;
  return (
    <div className="compendium-scroll" style={{ flex: 1, overflowY: "auto", minWidth: 0 }}>
      <div style={{ padding: "16px 20px", borderBottom: "2px solid #111", display: "flex", alignItems: "baseline", gap: 12 }}>
        <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: 2, color: "#c0240f" }}>QUERYING OMNINET…</div>
        <div style={{ fontSize: 15, fontWeight: 800, color: "#111" }}>“{search.echo}”</div>
        <div style={{ flex: 1 }} />
        <div style={{ fontFamily: MONO, fontSize: 10, color: "#999", letterSpacing: 1 }}>{search.summary}</div>
      </div>
      {search.total === 0 && (
        <div style={{ padding: "48px 20px", textAlign: "center", fontFamily: MONO, fontSize: 12, letterSpacing: 1, color: "#999" }}>
          NO RECORDS MATCH — refine the query.
        </div>
      )}
      {search.groups.map((g) => (
        <div key={g.label} style={{ borderBottom: "1px solid #e3ddcf" }}>
          <div style={{ padding: "9px 20px", background: "#f6f4ee", fontFamily: MONO, fontSize: 9, letterSpacing: 2, color: "#777", borderBottom: "1px solid #e3ddcf" }}>
            {g.label} · {g.n}
          </div>
          {g.items.map((it) => {
            const focused = ++i === focusIdx;
            return (
              <button
                key={`${it.type}/${it.id}`}
                ref={focused ? focusedRowRef : undefined}
                tabIndex={focused ? 0 : -1}
                type="button"
                className="compendium-hover-row"
                onClick={() => onPick({ type: it.type, id: it.id })}
                style={{ display: "flex", alignItems: "center", gap: 11, width: "100%", border: "none", borderBottom: "1px solid #efe9db", background: focused ? "#f4f1e8" : "#fff", cursor: "pointer", padding: "11px 20px", textAlign: "left" }}
              >
                <span style={{ flex: "none", width: 6, height: 6, background: it.dot }} />
                <span style={{ fontSize: 14, fontWeight: 700, color: "#111" }}><ValText text={it.name} /></span>
                <span style={{ fontFamily: MONO, fontSize: 10, color: "#999", letterSpacing: ".5px" }}>{it.sub}</span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function ListPane({
  browse,
  wide,
  state,
  dispatch,
  onPick,
  activeRowRef,
  focusIdx,
  focusedRowRef,
}: {
  browse: ReturnType<typeof computeBrowse>;
  wide: boolean;
  state: CompendiumState;
  dispatch: (a: Action) => void;
  onPick: (ref: Ref) => void;
  /** Attached to the selected row so a cross-link can scroll it into view. */
  activeRowRef: RefObject<HTMLButtonElement | null>;
  /** Roving keyboard-focus index over the flattened rows (group order, or flat order), or -1. */
  focusIdx: number;
  /** Attached to the focused row so a keyboard move can `.focus()` it (roving tabindex). */
  focusedRowRef: RefObject<HTMLButtonElement | null>;
}) {
  // Running index across maker groups — must match `orderedRefs(BrowseView)`'s grouped flatten so
  // the roving cursor lands on the same row the keyboard handler selects. Reset per render.
  let gi = -1;
  return (
    <div style={{ flex: "none", width: wide ? 316 : "100%", borderRight: wide ? "2px solid #111" : "none", display: "flex", flexDirection: "column", minWidth: 0, background: "#fff" }}>
      {/* filter + facets */}
      <div style={{ borderBottom: "2px solid #111", padding: "11px 13px 10px", background: "#faf8f3" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, border: "2px solid #111", background: "#fff", padding: "0 9px", height: 32 }}>
          <Icon icon={faChevronRight} className="compendium-filter-icon" />
          <input
            value={state.filter}
            onChange={(e) => dispatch({ t: "filter", v: e.target.value })}
            placeholder={`filter ${browse.catLabelLow}…`}
            style={{ flex: 1, minWidth: 0, border: "none", background: "none", fontFamily: MONO, fontSize: 11, color: "#111", outline: "none" }}
          />
          <span style={{ fontFamily: MONO, fontSize: 10, color: "#bbb" }}>{browse.listCount}</span>
        </div>
        {browse.facetAxes.map((axis) => (
          <div key={axis.key} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginTop: 9 }}>
            <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: ".5px", color: "#999", minWidth: 36 }}>{axis.label}</span>
            {axis.chips.map((f) => (
              <button
                key={f.val}
                type="button"
                className="compendium-hover-facet"
                onClick={() => dispatch({ t: "facet", axis: axis.key, val: f.val })}
                style={{ border: `1.5px solid ${f.on ? "#111" : "#c9c3b5"}`, background: f.on ? "#111" : "#fff", color: f.on ? "#fff" : "#111", cursor: "pointer", padding: "4px 9px", fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: ".5px" }}
              >
                {f.label}
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* empty / flat / grouped */}
      {browse.isEmptyCat ? (
        <div className="compendium-scroll" style={{ flex: 1, overflowY: "auto", padding: "34px 22px", textAlign: "center" }}>
          <div style={{ width: 54, height: 54, margin: "0 auto 18px", border: "2px solid #111", background: "repeating-linear-gradient(45deg,#f2f0ea,#f2f0ea 7px,#e7e4dc 7px,#e7e4dc 14px)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: MONO, fontSize: 9, color: "#999", letterSpacing: 1 }}>∅</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#111", marginBottom: 8 }}>NO {browse.catUpper} LOADED</div>
          <div style={{ fontFamily: MONO, fontSize: 11, lineHeight: 1.7, color: "#888", letterSpacing: ".3px", maxWidth: 240, margin: "0 auto" }}>
            This corpus arrives with the GM's pack. Drop the purchased <b style={{ color: "#666" }}>.lcp</b> into the world folder and the archive populates.
          </div>
        </div>
      ) : browse.showGrouped ? (
        <div className="compendium-scroll" style={{ flex: 1, overflowY: "auto" }}>
          {browse.groups.map((g) => (
            <div key={g.name}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 13px", background: g.tint, borderBottom: "2px solid #111", borderTop: "2px solid #111" }}>
                <span style={{ width: 9, height: 9, background: g.accent }} />
                <span style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: 1, color: "#111" }}>{g.name}</span>
                <span style={{ flex: 1 }} />
                <span style={{ fontFamily: MONO, fontSize: 9, color: "#999" }}>{g.n}</span>
              </div>
              {g.items.map((it) => {
                const focused = ++gi === focusIdx;
                return (
                  <button
                    key={it.id}
                    ref={focused ? focusedRowRef : it.on ? activeRowRef : undefined}
                    tabIndex={focused ? 0 : -1}
                    type="button"
                    className="compendium-hover-row"
                    onClick={() => onPick({ type: it.type, id: it.id })}
                    style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", border: "none", borderBottom: "1px solid #ece6d8", background: it.on || focused ? "#f4f1e8" : "#fff", cursor: "pointer", padding: "10px 13px", textAlign: "left" }}
                  >
                    <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: "#111", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}><ValText text={it.name} /></span>
                    <span style={{ fontFamily: MONO, fontSize: 9, color: "#999" }}>{it.llTag}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      ) : (
        <div className="compendium-scroll" style={{ flex: 1, overflowY: "auto" }}>
          {browse.listRows.map((it, idx) => {
            const focused = idx === focusIdx;
            return (
              <button
                key={it.id}
                ref={focused ? focusedRowRef : it.on ? activeRowRef : undefined}
                tabIndex={focused ? 0 : -1}
                type="button"
                className="compendium-hover-row"
                onClick={() => onPick({ type: it.type, id: it.id })}
                style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", border: "none", borderBottom: "1px solid #ece6d8", background: it.on || focused ? "#f4f1e8" : "#fff", cursor: "pointer", padding: "11px 13px", textAlign: "left" }}
              >
                <span style={{ flex: "none", width: 4, alignSelf: "stretch", background: it.bar }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 14, fontWeight: 700, color: "#111", lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}><ValText text={it.name} /></span>
                  <span style={{ display: "block", fontFamily: MONO, fontSize: 10, color: "#8c887e", letterSpacing: ".3px", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.sub}</span>
                </span>
                <span style={{ flex: "none", fontFamily: MONO, fontSize: 8, letterSpacing: ".5px", color: "#b3ad9f", border: "1px solid #ddd6c7", padding: "2px 4px" }}>{it.pack}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function WelcomePane({ source }: { source: CompendiumSource }) {
  const tiles = [
    { n: source.count("frame"), label: "FRAMES" },
    { n: source.count("weapon"), label: "WEAPONS" },
    { n: source.count("system") + source.count("mod"), label: "SYS · MODS" },
    { n: source.count("talent") + source.count("skill") + source.count("pilot_gear"), label: "PILOT" },
  ];
  return (
    <div style={{ padding: "30px 26px" }}>
      <div style={{ position: "relative", overflow: "hidden", border: "2px solid #111", background: "#111", color: "#fff", padding: 26 }}>
        <div style={{ position: "absolute", inset: 0, opacity: 0.1, background: "repeating-linear-gradient(0deg,#fff,#fff 1px,transparent 1px,transparent 4px)" }} />
        <div className="compendium-sweep" style={{ position: "absolute", left: 0, right: 0, top: 0, height: "34%", background: "linear-gradient(#fff,transparent)", opacity: 0.05 }} />
        <div style={{ position: "relative" }}>
          <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: 3, color: "#c0240f" }}>OMNINET // CORPUS ARCHIVE</div>
          <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: "-.5px", lineHeight: 1.04, marginTop: 8 }}>
            Every system the<br />game has loaded —<br />indexed and linked.
          </div>
          <div style={{ fontFamily: MONO, fontSize: 11, lineHeight: 1.7, color: "#b8b3a6", marginTop: 14, maxWidth: 430 }}>
            Pick a section above, choose a category, follow any cross-reference. Or press <b style={{ color: "#fff" }}>/</b> to query the whole archive at once.
          </div>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", border: "2px solid #111", borderTop: "none" }}>
        {tiles.map((c, i) => (
          <div key={c.label} style={{ padding: "15px 14px", borderRight: i < 3 ? "2px solid #111" : "none", borderTop: "2px solid #111" }}>
            <div style={{ fontSize: 27, fontWeight: 800, color: "#111", lineHeight: 1 }}>{c.n}</div>
            <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 1.5, color: "#888", marginTop: 6 }}>{c.label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16, fontFamily: MONO, fontSize: 10, letterSpacing: 1, color: "#a39e92" }}>
        <span style={{ width: 6, height: 6, background: "#c0240f", borderRadius: "50%" }} />
        LINK ESTABLISHED · READ-ONLY · {PACK_LINE}
      </div>
    </div>
  );
}

// ---- misc ---------------------------------------------------------------------------

const SECTION_FIRST: Record<SectionId, CompendiumType> = {
  MECHS: "frame",
  PILOTS: "talent",
  NPCS: "npc_class",
  GLOSSARY: "tag",
};

function computeSections(source: CompendiumSource, active: SectionId) {
  return (Object.keys(SECTIONS) as SectionId[]).map((id) => ({
    id,
    count: SECTIONS[id].reduce((a, [t]) => a + source.count(t), 0),
    on: id === active,
  }));
}
